import {
    Colors,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import { BAR_MIN_WIDTH_PX, BADGE_TOOLTIP_THRESHOLD, TOOLTIP_DELAY_MS } from './barChart.constants';
import { QuestionTextRenderer } from '../QuestionTextRenderer/QuestionTextRenderer';

import type { ColorType } from '@amelio/ui-library';

interface BarRenderData {
    heightPercentage: number;
    gradient: string;
}

interface BarChartValue {
    /** Label displayed under the bar */
    label: string;
    /** Numeric value for bar height */
    value: number;
    /** Badge background color */
    badgeColor: ColorType;
    /** Badge text color */
    badgeTextColor: ColorType;
    /** Optional icon displayed above the bar (e.g. emoji) */
    icon?: string;
}

interface BarChartProps {
    /** Array of bar data ordered by value range */
    values: BarChartValue[];
    /** Y-axis values to display */
    yValues: number[];
    /** Maximum Y value for scaling */
    maxYValue?: number;
    /** Minimum Y value for scaling (dynamic Y-axis support) */
    minYValue?: number;
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

const BarChart = ({
    values,
    yValues,
    maxYValue,
    minYValue = 0,
    className = '',
    dataTestId = 'bar-chart',
}: BarChartProps) => {
    const maxValue = maxYValue || Math.max(...yValues, ...values.map((v) => v.value));
    const range = maxValue - minYValue;

    const getBarHeight = (value: number): number => {
        if (range === 0) return 0;
        return ((value - minYValue) / range) * 100;
    };

    const getBarGradient = (value: number): string => {
        const heightPercentage = getBarHeight(value);

        if (heightPercentage <= 10) {
            return Colors.SECONDARY_900;
        }

        if (heightPercentage <= 30) {
            return `linear-gradient(to top, ${Colors.SECONDARY_900} 0%, ${Colors.SECONDARY} 100%)`;
        }

        return `linear-gradient(to top, ${Colors.SECONDARY_900} 0%, ${Colors.SECONDARY} 50%, ${Colors.SECONDARY_400} 100%)`;
    };

    const getBarRenderData = (value: number): BarRenderData => ({
        heightPercentage: getBarHeight(value),
        gradient: getBarGradient(value),
    });

    const getBarStyles = (heightPercentage: number, gradient: string) => {
        const hasHeight = heightPercentage > 0;
        return {
            height: hasHeight ? `${heightPercentage}%` : '0',
            minHeight: hasHeight ? '0' : '3px',
            background: gradient,
        };
    };

    const barsMinWidth = values.length * BAR_MIN_WIDTH_PX;

    return (
        <div
            className={`w-full ${className}`}
            data-testid={dataTestId}
        >
            <div className={'flex h-full'}>
                <div className={'flex h-[300px] w-12 flex-col justify-between pr-16 text-right'}>
                    {[...yValues].reverse().map((yValue, index) => (
                        <Text
                            key={`y-axis-${index}`}
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'flex items-center justify-end leading-none'}
                            data-testid={`${dataTestId}-y-value-${yValue}`}
                        >
                            {yValue}
                        </Text>
                    ))}
                </div>

                <div className={'scrollbar-glass -mx-9 max-w-full flex-1 overflow-x-auto pb-2.5'}>
                    <div
                        className={'relative flex flex-col px-9'}
                        style={{
                            minWidth: `${barsMinWidth}px`,
                        }}
                    >
                        <div className={'relative flex h-[300px] flex-col'}>
                            <div className={'absolute inset-0 flex flex-col justify-between'}>
                                {[...yValues].reverse().map((yValue, index) => (
                                    <div
                                        key={`grid-line-${index}`}
                                        className={'border-base-200 w-full border-t'}
                                        data-testid={`${dataTestId}-grid-line-${yValue}`}
                                    />
                                ))}
                            </div>

                            <div
                                className={
                                    'relative flex h-full items-end justify-around gap-1 pr-4'
                                }
                            >
                                {values.map(({ value, icon }, index) => {
                                    const { heightPercentage, gradient } = getBarRenderData(value);

                                    return (
                                        <div
                                            key={index}
                                            className={'flex min-w-0 flex-1 flex-col items-center'}
                                            data-testid={`${dataTestId}-bar-${index}`}
                                        >
                                            <div
                                                className={
                                                    'relative flex h-[300px] w-16 flex-col items-center justify-end'
                                                }
                                            >
                                                {icon && (
                                                    <img
                                                        src={icon}
                                                        alt={''}
                                                        className={'mb-1 h-8 w-8'}
                                                        data-testid={`${dataTestId}-bar-icon-${index}`}
                                                    />
                                                )}

                                                <div
                                                    className={'w-full rounded-t-md'}
                                                    style={getBarStyles(heightPercentage, gradient)}
                                                    data-testid={`${dataTestId}-bar-fill-${index}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                            <div
                                className={
                                    'border-base-200 flex justify-around gap-1 border-t pt-3 pr-4 pb-4'
                                }
                            >
                                {values.map(({ label, badgeColor, badgeTextColor }, index) => {
                                    const badge = (
                                        <div
                                            className={
                                                'max-w-[120px] truncate rounded-full px-3 py-1 text-xs font-medium'
                                            }
                                            style={{
                                                backgroundColor: badgeColor,
                                                color: badgeTextColor,
                                            }}
                                            data-testid={`${dataTestId}-badge-${index}`}
                                        >
                                            <QuestionTextRenderer text={label} />
                                        </div>
                                    );

                                    return (
                                        <div
                                            key={`badge-${index}`}
                                            className={'flex min-w-0 flex-1 justify-center'}
                                        >
                                            {label.length > BADGE_TOOLTIP_THRESHOLD ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>{badge}</TooltipTrigger>

                                                    <TooltipContent
                                                        side={'top'}
                                                        className={'max-w-xs'}
                                                        data-testid={`${dataTestId}-badge-tooltip-${index}`}
                                                    >
                                                        <QuestionTextRenderer text={label} />
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                badge
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </TooltipProvider>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { BarChart };
export type { BarChartProps, BarChartValue };
export default BarChart;
